import { useQuery } from '@tanstack/react-query';
import { parseAsStringLiteral, useQueryStates } from 'nuqs';
import {
  NOTIFICATION_STATUSES,
  NOTIFICATION_TYPES,
  type NotificationItem,
  type NotificationStatus,
  type NotificationType,
  type Paginated,
} from '@barbeiropro/contracts';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { useTableParams } from '@/components/shared/use-table-params';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { cn } from '@/lib/utils';

export const TYPE_LABELS: Record<NotificationType, string> = {
  invite: 'Convite de equipe',
  password_reset: 'Redefinição de senha',
  appointment_confirmation: 'Confirmação de agendamento',
  appointment_requested: 'Agendamento solicitado',
  appointment_rescheduled: 'Agendamento remarcado',
  appointment_cancelled: 'Agendamento cancelado',
  appointment_reminder: 'Lembrete de agendamento',
  financial_alert: 'Alerta financeiro',
  campaign: 'Campanha',
  contact_message: 'Mensagem de contato',
};

export const STATUS_LABELS: Record<NotificationStatus, string> = {
  sent: 'Enviado',
  queued: 'Na fila',
  failed: 'Falhou',
  skipped_unconfigured: 'Não enviado: envio indisponível',
  skipped_optout: 'Não enviado: cliente optou por não receber',
  skipped_invalid_phone: 'Não enviado: telefone inválido',
  skipped_disabled: 'Não enviado: envio desligado',
  skipped_limit: 'Não enviado: limite diário atingido',
};

const STATUS_STYLES: Record<NotificationStatus, string> = {
  sent: 'bg-accent text-accent-foreground ring-primary/25',
  queued: 'bg-brass/15 text-amber-ink ring-brass/40',
  failed: 'bg-oxblood/10 text-oxblood-ink ring-oxblood/30',
  skipped_unconfigured: 'bg-muted text-muted-foreground ring-border',
  skipped_optout: 'bg-muted text-muted-foreground ring-border',
  skipped_invalid_phone: 'bg-muted text-muted-foreground ring-border',
  skipped_disabled: 'bg-muted text-muted-foreground ring-border',
  skipped_limit: 'bg-muted text-muted-foreground ring-border',
};

function NotificationStatusBadge({ status }: { status: NotificationStatus }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset', STATUS_STYLES[status])}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

const ALL = 'all';

export function NotificationsPage() {
  const tz = useTimezone();
  const params = useTableParams({ sort: 'createdAt', order: 'desc' });
  const [filters, setFilters] = useQueryStates(
    { status: parseAsStringLiteral(NOTIFICATION_STATUSES), type: parseAsStringLiteral(NOTIFICATION_TYPES) },
    { history: 'replace' },
  );

  const query = useQuery({
    queryKey: ['notifications', params.query, filters.status, filters.type],
    queryFn: () => api.get<Paginated<NotificationItem>>('/notifications', { ...params.query, status: filters.status ?? undefined, type: filters.type ?? undefined }),
  });

  const columns: DataColumn<NotificationItem>[] = [
    { header: 'Tipo', accessorKey: 'type', meta: { sortKey: 'type' }, cell: ({ row }) => TYPE_LABELS[row.original.type] ?? row.original.type },
    { header: 'Destinatário', accessorKey: 'recipientMasked', cell: ({ row }) => <span className="font-mono text-xs">{row.original.recipientMasked}</span> },
    {
      header: 'Situação',
      accessorKey: 'status',
      meta: { sortKey: 'status' },
      cell: ({ row }) => (
        <div className="grid gap-0.5">
          <NotificationStatusBadge status={row.original.status} />
          {row.original.lastError && <span className="max-w-64 truncate text-xs text-muted-foreground" title={row.original.lastError}>{row.original.lastError}</span>}
        </div>
      ),
    },
    { header: 'Tentativas', accessorKey: 'attempts', meta: { sortKey: 'attempts', align: 'right' } },
    { header: 'Criada em', accessorKey: 'createdAt', meta: { sortKey: 'createdAt' }, cell: ({ row }) => <span className="tabular">{fmtDateTime(row.original.createdAt, tz)}</span> },
    { header: 'Enviada em', accessorKey: 'sentAt', meta: { sortKey: 'sentAt' }, cell: ({ row }) => (row.original.sentAt ? <span className="tabular">{fmtDateTime(row.original.sentAt, tz)}</span> : <span className="text-muted-foreground">-</span>) },
  ];

  return (
    <div>
      <PageHeader title="Notificações" description="E-mails disparados pelo sistema. Destinatários aparecem mascarados." />
      <DataTable
        columns={columns}
        data={query.data}
        params={params}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => query.refetch()}
        searchPlaceholder="Buscar destinatário"
        empty={{ title: 'Nenhuma notificação ainda', description: 'Convites, confirmações e lembretes aparecem aqui assim que forem gerados.' }}
        filters={
          <>
            <Select value={filters.status ?? ALL} onValueChange={(v) => { void setFilters({ status: v === ALL ? null : (v as NotificationStatus) }); void params.set({ page: 1 }); }}>
              <SelectTrigger size="sm" className="w-56" aria-label="Filtrar por situação"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas as situações</SelectItem>
                {NOTIFICATION_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.type ?? ALL} onValueChange={(v) => { void setFilters({ type: v === ALL ? null : (v as NotificationType) }); void params.set({ page: 1 }); }}>
              <SelectTrigger size="sm" className="w-56" aria-label="Filtrar por tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos os tipos</SelectItem>
                {NOTIFICATION_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        }
      />
    </div>
  );
}
